import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { H2, H3 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { Button } from "../../components/Button";
import { Box } from "../../components/Box";
import { Card } from "../../components/Card";
import { DateTime } from "luxon";
import { Flex } from "../../components/Flex";
import { Image } from "../../components/Image";
import { monitoringEventSummary } from "../../utils/screeningChecks";
import { useScreeningProvider } from "../../data/useScreeningProvider";
import { capitalise } from "../../utils/string";
import { Modal } from "../../components/Modal";
import { ROUTE } from "../../routes";
import { showError } from "../../utils/error-handling";
import { NewCompanyMonitoringRecordFrame } from "./NewCompanyMonitoringRecordFrame";
import { useCreateEntityMonitoring } from "../../data/useCreateEntityMonitoring";
import { useCountries } from "../../data/useCountries";
import { useEntityTypes } from "../../data/useEntityTypes";

const Header = () => {
  return (
    <>
      <H2>Monitoring</H2>
      <Paragraph>
        Please confirm the details for this monitoring record.
      </Paragraph>
    </>
  );
};

export const NewCompanyMonitoringSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();
  const [showSuccess, setShowSuccess] = useState(false);
  const [doubleClick, setDoubleClick] = useState(false);

  const formData = location.state?.form;

  const { isLexisNexis2 } = useScreeningProvider();

  const monitoringEvents = monitoringEventSummary({
    pepRequired: formData?.pep_and_sanction_required,
    adverseRequired: formData?.adverse_media_required,
    isEntity: true,
    isLexisNexis2,
  });

  const { mutate: createEntityMonitoring } = useCreateEntityMonitoring();

  const handleRunChecks = () => {
    setDoubleClick(true);
    const date_of_incorporation = formData.date_of_incorporation
      ? DateTime.fromJSDate(formData.date_of_incorporation as Date).toFormat(
          "yyyy-MM-dd"
        )
      : null;

    createEntityMonitoring(
      {
        company_name: formData.company_name,
        country: formData.country,
        entity_type: formData.entity_type,
        registration_number: formData.registration_number,
        date_of_incorporation: date_of_incorporation,
        external_reference: formData.yourReference?.trim() || null,
        individual_reference: null,
        pep_and_sanction_required: formData.pep_and_sanction_required,
        adverse_media_required: formData.adverse_media_required,
        primary_division: formData.division,
        primary_team: formData.team,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
        },
        onError: (err: any) => {
          setDoubleClick(false);
          showError(
            err,
            "A problem occurred while creating the entity monitoring record."
          );
        },
      }
    );
  };

  const navigateToNewCompanyMonitoringFlow = () => {
    navigate(ROUTE.MONITORING_NEW_COMPANY);
  };

  const navigateToMonitoring = () => {
    setShowSuccess(false);
    navigate(ROUTE.MONITORING);
  };

  // Map country ID to country object (with icon)
  const country = countries?.data.find((c) => c.id === formData.country);
  const countryLabel = country?.name;
  const countryFlag = country?.icon;

  // Map entity_type ID to its name
  const entityType = entityTypes?.data.find(
    (e) => e.id === formData.entity_type
  );
  const entityTypeName = entityType?.name;

  return (
    <NewCompanyMonitoringRecordFrame step="summary">
      <Header />
      {formData && (
        <>
          <Box>
            <Card mb={2}>
              <H3 fontSize={2}>{formData?.company_name}</H3>

              {formData?.entity_type && (
                <Paragraph my={1} color={"gray.60"}>
                  {entityTypeName}
                </Paragraph>
              )}
              {formData?.registration_number && (
                <Paragraph my={1} color={"gray.60"}>
                  {formData?.registration_number}
                </Paragraph>
              )}
              {formData?.date_of_incorporation && (
                <Paragraph color={"gray.60"} my={1}>
                  {DateTime.fromJSDate(
                    formData?.date_of_incorporation
                  ).toFormat("dd/MM/yyyy")}
                </Paragraph>
              )}

              {formData?.country && (
                <Flex gap="6" alignItems="center" my={1}>
                  <img
                    src={`data:image/png;base64,${countryFlag}`}
                    alt="iamge"
                  />
                  <Paragraph my={1} color={"gray.60"}>
                    {capitalise(countryLabel)}
                  </Paragraph>
                </Flex>
              )}
            </Card>

            <Card mb={2}>
              <H3 fontSize={2}>Monitoring events</H3>
              {monitoringEvents.map(({ key, label, icon }) => (
                <Flex gap="16" key={key}>
                  <Image src={icon} alt={label} height="auto" mr={1} />
                  <Paragraph my={1} fontSize={1} color="gray.60">
                    {label}
                  </Paragraph>
                </Flex>
              ))}
            </Card>
          </Box>

          <Flex justifyContent={"flex-end"} mt={2}>
            <Button onClick={handleRunChecks} isDisabled={doubleClick}>
              Confirm
            </Button>
          </Flex>
        </>
      )}

      <Modal
        isOpen={showSuccess}
        onClose={navigateToMonitoring}
        title="Monitoring record created successfully!"
        content="Thank you for creating a monitoring record for this company. The details of the initial check will be available shortly. You can create another record now or go back to the dashboard to view the results."
        onModalConfirmClick={navigateToNewCompanyMonitoringFlow}
        secondaryButtonText="Close"
        primaryButtonText="+ New Monitoring Record"
        primaryButtonVariant="primary"
        hasSecondaryButton
        onSecondaryButtonClick={navigateToMonitoring}
        width="450px"
      />
    </NewCompanyMonitoringRecordFrame>
  );
};
